import { Fragment, useState } from "react";
import { useInView } from "react-intersection-observer";
import { RouteFactory } from "@app/routePaths";
import { ArticleDataObject, Tag } from "@app/types/Cue";
import { ArticleContext } from "@app/types/Page";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import { AdUnitNames } from "@components/Advertisement/utils/types";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import Header from "@components/Header/Header";
import {
  getSectionNavigationPath,
  useArticleHeaderState,
} from "@components/Header/helpers";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useArticleGiftReceived from "@hooks/useArticleGiftReceived";
import { useDelayedComponents } from "@hooks/useDelayedComponents";
import useGenerateGiftURL from "@hooks/useGenerateGiftURL";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import useIsOutbrainEnabled from "@hooks/useIsOutbrainEnabled";
import { useNextArticle } from "@hooks/useNextArticle";
import { getFormattedTextForAds } from "@pages/Section/helpers";
import useOKTAUserStore from "@store/useOKTAUserStore";
import { getParentOfSection, getSectionUrlPath } from "@util/helpers";

import AppBanner from "./components/AppBanner";
import ArticleSubshareModal from "./components/ArticleSubshare/ArticleSubshareModal";
import ArticleSubsharePrompt from "./components/ArticleSubshare/ArticleSubsharePrompt";
import ArticleSubshareReceived from "./components/ArticleSubshare/ArticleSubshareReceived";
import { LAMBDA_RESPONSE_TYPES } from "./types/Alacrity";
import {
  ArticleFactory,
  getArticleMainSectionPath,
  VerticalHeaderFactory,
} from "./utils/helpers";

export type ArticleContentProps = Pick<ArticleContext, "data">;

export default function ArticleContent({
  data: {
    cook: {
      data: { context },
    },
    sectionNews,
    seeAlso,
    isPreview,
    seoFooterData,
  },
}: ArticleContentProps): React.ReactElement {
  const [showGiftSubscribePrompt, setShowGiftSubscribePrompt] = useState(false);
  const [articleInView, setArticleInView] =
    useState<ArticleDataObject>(context);
  const { isModalOpen, setIsModalOpen, alacrityRes, isGiftReceived } =
    useArticleGiftReceived();

  const isOutbrainEnabled = useIsOutbrainEnabled();
  const { isShown } = useDelayedComponents(800);

  const { articleTopRef, isShowArticleHeader } = useArticleHeaderState();
  const { isLoading, moreStories, ref } = useNextArticle(context, sectionNews);

  const { gsChannels } = useGrapeshotData(moreStories[0].context.urlPath, -1);

  // Article data de-construct
  const { sections } = context;

  // The main section of the article.
  const mainSection = articleInView.sections?.[0] || sections?.[0];
  const mainSectionURLPath = getArticleMainSectionPath(mainSection);

  // The parent section if main section has parent, otherwise this is also the main section.
  const parentSection = getParentOfSection(mainSection) || mainSection;
  const parentSectionUrlPath = getSectionUrlPath(parentSection);

  const verticalPath = mainSectionURLPath || "";
  const isThisArticlegifted =
    alacrityRes?.data.responseType ===
      LAMBDA_RESPONSE_TYPES.SUCCESS_VALID_GIFT_TOKEN &&
    !alacrityRes.data.viewGiftStatus;

  const VerticalHeaderLayout = VerticalHeaderFactory({ verticalPath });

  const { ref: showGiftSubscribePromptRef } = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      if (inView && isThisArticlegifted) setShowGiftSubscribePrompt(true);
    },
  });

  const handleSetArticle = (e: ArticleDataObject) => {
    setArticleInView(e);
  };

  const sectionPath = getSectionNavigationPath(
    context.tags || [],
    parentSectionUrlPath
  );

  const disablePrestitialBasedOnUrlPath = [
    RouteFactory.paidPressRelease, // paid press release pages
    RouteFactory.section("events-awards"), // events-awards pages
  ].some((path) => context?.urlPath.startsWith(path));

  const disablePrestitialBasedOnTags = [RouteFactory.keywordsBudget2025].some(
    (tags) => context?.tags?.some((tag) => tag.urlPath === tags)
  );

  const adsNames: AdUnitNames[] =
    disablePrestitialBasedOnUrlPath || disablePrestitialBasedOnTags
      ? ["lb1", "imu1", "imu2", "imu3", "midarticlespecial", "catfish", "abm"]
      : [
          "lb1",
          "imu1",
          "imu2",
          "imu3",
          "midarticlespecial",
          "prestitial",
          "catfish",
          "abm",
        ];

  const contentAccess = articleInView?.paywall?.contentAccess || "0";
  const isPremium = contentAccess === "1";

  const {
    alacrityRes: giftURlAlacrityRes,
    isModalOpen: giftURlisModalOpen,
    setIsModalOpen: setGiftURLIsModalOpen,
    handleGenerateGiftURLModal: handleGenerateGiftURLModal,
  } = useGenerateGiftURL(isPremium);

  const articleInViewGiftRes = giftURlAlacrityRes.find(
    (res) => res.pathname === articleInView.urlPath
  );

  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);

  return (
    <div className="w-full" data-testid="article-content-component">
      <AdSettings
        adNames={adsNames}
        path={mainSectionURLPath || "/"}
        pageType="articles"
        adTargets={[{ key: "page", value: "article" }]}
        slotTargets={[
          { key: "btarticleid", value: context?.id },
          {
            key: "bttags",
            value:
              context?.tags
                ?.map((tag: Tag) => getFormattedTextForAds(tag.name))
                .join(",") || "",
          },
          { key: "gs_channels", value: gsChannels },
        ]}
      />

      <Ad
        adType="lb1"
        className="flex min-h-[50px] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 md:py-3 lg:min-h-[250px]"
      />

      <Header
        article={articleInView}
        displayArticle={isShowArticleHeader}
        parentCategory={sectionPath}
        {...{
          handleGenerateGiftURLModal,
          setIsModalOpen: setGiftURLIsModalOpen,
          alacrityRes: articleInViewGiftRes?.alacrityRes,
          isPremium,
          userType: OKTAUserInfo?.usertype,
        }}
      />

      <PulseEntry />

      <>
        {VerticalHeaderLayout ? (
          <Container rootClassName="py-2">
            <VerticalHeaderLayout />
          </Container>
        ) : null}

        <div id="article-content-wrapper" className="w-full">
          {moreStories.map((article, index) => {
            // Check if article is undefined
            if (!article.context) return null;

            const displayType = article.context.displaySetting?.displayType;
            const path = article.context.urlPath;
            const ArticleLayout = ArticleFactory({ displayType, path });

            if (!ArticleLayout) return null;

            const giftURLRes = giftURlAlacrityRes.find(
              (res) => res.pathname === article.context.urlPath
            );

            return (
              <Fragment key={article.context.id}>
                <ArticleLayout
                  article={article.context}
                  isGiftReceived={isGiftReceived}
                  sectionNews={article.sectionNews}
                  variant="default"
                  index={index}
                  seeAlso={seeAlso}
                  isNavigate={moreStories.length > 1}
                  setArticleObj={(e) => {
                    handleSetArticle(e);
                  }}
                  isOutbrainEnabled={isOutbrainEnabled}
                  isPreview={isPreview}
                  gsChannels={gsChannels}
                  articleInView={articleInView}
                  handleGenerateGiftURLModal={handleGenerateGiftURLModal}
                  setIsModalOpen={setGiftURLIsModalOpen}
                  alacrityRes={giftURLRes?.alacrityRes}
                  isModalOpen={giftURlisModalOpen}
                  isPremium={isPremium}
                  displayArticleRef={articleTopRef}
                />

                <div ref={ref}></div>
              </Fragment>
            );
          })}
        </div>

        <ArticleSubsharePrompt
          isModalOpen={showGiftSubscribePrompt}
          setIsModalOpen={setShowGiftSubscribePrompt}
        />

        <div
          aria-roledescription="article-subshare-prompt-observer"
          ref={showGiftSubscribePromptRef}
        ></div>

        {isLoading ? (
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} size="5x" spin className="mb-6" />
          </div>
        ) : null}

        <SeoFooter data={seoFooterData} />

        <Ad adType="catfish" />

        <Ad adType="abm" />

        <Ad adType="prestitial" />

        <>
          {alacrityRes ? (
            <ArticleSubshareModal
              isModalOpen={isModalOpen && !alacrityRes.data.viewGiftStatus}
              setIsModalOpen={setIsModalOpen}
            >
              <ArticleSubshareReceived
                alacrityRes={alacrityRes}
                setIsModalOpen={setIsModalOpen}
              />
            </ArticleSubshareModal>
          ) : null}
        </>
      </>

      {isShown ? <AppBanner /> : null}
    </div>
  );
}
